// src/client/screens/borrower/LoanConfirmScreen.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ChevronLeft } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import {
  PROVIDER_DISPLAY_NAME, SUPPORTED_MM_PROVIDERS, type MobileMoneyProvider,
} from '@/constants/config';
import { computeAllInCost, computeRepaymentSchedule, formatEAR } from '@client/utils/loanUtils';
import { applyLoan } from '@client/services/loanService';
import { readLoanDraft, clearLoanDraft, type LoanDraft } from '@client/lib/loanDraft';
import { type Ngwee } from '@/lib/types';
import { useAuth } from '@client/auth/useAuth';
import { supabase } from '@client/lib/supabase';
import Button from '@client/components/Button';
import { useToast } from '@client/components/ToastProvider';
import { ngweeToKwachaNumber } from '@client/utils/currency';

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

// "+260971234567" → "+260 97 *** 4567"
function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return phone;
  const country = digits.slice(0, 3);
  const prefix  = digits.slice(3, 5);
  const last4   = digits.slice(-4);
  return `+${country} ${prefix} *** ${last4}`;
}

// "•••4567"
function shortMask(phone: string): string {
  const last4 = phone.replace(/\D/g, '').slice(-4);
  return `•••${last4}`;
}

// Looks up an existing payment method for the given provider or creates one.
async function getOrCreatePaymentMethod(
  provider: MobileMoneyProvider,
  phone: string,
  profileId: string,
): Promise<string> {
  const nowIso = new Date().toISOString();

  const { data: existing } = await supabase
    .from('payment_methods')
    .select('id, verified_at')
    .eq('user_id', profileId)
    .eq('provider', provider)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();

  if (existing) {
    if (!existing.verified_at) {
      await supabase
        .from('payment_methods')
        .update({ verified_at: nowIso })
        .eq('id', existing.id);
    }
    return existing.id as string;
  }

  const { data: anyMethod } = await supabase
    .from('payment_methods')
    .select('id')
    .eq('user_id', profileId)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();

  const { data: created, error } = await supabase
    .from('payment_methods')
    .insert({
      user_id: profileId,
      provider,
      masked_number: maskPhone(phone),
      full_number_encrypted: phone,
      is_default: !anyMethod,
      verified_at: nowIso,
    })
    .select('id')
    .single();

  if (error || !created) throw new Error('Failed to create payment method');
  return created.id as string;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function LoanConfirmScreen() {
  const [draft, setDraft] = useState<LoanDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [provider, setProvider] = useState<MobileMoneyProvider>(SUPPORTED_MM_PROVIDERS[0]);
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useAuth();

  useEffect(() => {
    const d = readLoanDraft();
    if (!d) {
      navigate(ROUTES.loanApply, { replace: true });
      return;
    }
    setDraft(d);
  }, [navigate]);

  if (!draft) return null;

  const principalNgwee = draft.principalNgwee as Ngwee;
  const cost = computeAllInCost(principalNgwee, draft.termDays);
  const principalKwacha = ngweeToKwachaNumber(principalNgwee);

  // Preview the schedule starting from today (server uses real disbursed_at).
  const schedule = computeRepaymentSchedule(
    principalNgwee,
    draft.termDays,
    new Date().toISOString(),
  );

  const submit = async () => {
    if (!profile) {
      toast.show('Please sign in again', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const paymentMethodId = await getOrCreatePaymentMethod(provider, profile.phone, profile.id);
      const r = await applyLoan({
        principal: principalNgwee,
        termDays: draft.termDays,
        paymentMethodId,
      });
      if (!r.ok) {
        const msgMap: Record<string, string> = {
          concurrent_loan: 'You already have an active loan',
          cooling_off: 'Please wait 24 hours after repayment before applying again',
          kyc_not_verified: 'Your KYC verification is not complete',
          unauthorized: 'Your session expired — please sign in again',
          forbidden: 'This amount or term is not available for your tier yet',
          not_found: 'Account not found — please contact support',
          invalid_input: 'Invalid loan details',
          invalid_principal: 'Loan amount is invalid',
          invalid_term: 'Loan term is invalid',
          rate_limited: 'Too many attempts — please wait a moment',
          internal_error: 'Server error — please try again',
        };
        const [code, ...rest] = r.error.split('|');
        const detail = rest.join('|');
        const friendly = code && msgMap[code] ? msgMap[code] : `Could not submit (${code ?? 'unknown'})`;
        console.error('[apply-loan] failed:', r.error);
        toast.show(detail ? `${friendly} — ${detail}` : friendly, 'error');
        return;
      }
      clearLoanDraft();
      navigate(ROUTES.loanSuccess, { replace: true });
    } catch (e) {
      console.error('[apply-loan] exception:', e);
      const detail = e instanceof Error ? e.message : 'unexpected error';
      toast.show(`Could not submit: ${detail}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--c-bg)',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 480,
      margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 20px 8px', display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
        <button type="button" aria-label="Go back" style={ICON_BTN} onClick={() => navigate(-1)}>
          <ChevronLeft size={18} />
        </button>
        <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, color: 'var(--c-text-muted)' }}>
          review
        </span>
      </div>

      <div style={{ padding: '24px 28px', flex: 1 }}>
        <h1 style={{
          fontFamily: 'var(--ff-display)',
          fontSize: 38,
          lineHeight: 1,
          color: 'var(--c-text)',
          letterSpacing: -1,
          fontWeight: 400,
          margin: 0,
        }}>
          Looks<br />good?
        </h1>

        {/* Dark agreement card — the editorial centerpiece */}
        <div style={{
          marginTop: 28,
          background: '#0A2540',
          color: '#FFFFFF',
          borderRadius: 22,
          padding: '24px 22px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: -40, right: -40,
            width: 160, height: 160, borderRadius: '50%',
            border: '1px solid #FFFFFF', opacity: 0.08,
          }} aria-hidden />
          <div style={{
            position: 'absolute', top: -20, right: -20,
            width: 100, height: 100, borderRadius: '50%',
            border: '1px solid #60A5E8', opacity: 0.5,
          }} aria-hidden />

          <div style={{
            fontSize: 11,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            opacity: 0.6,
            fontWeight: 500,
          }}>
            Loan agreement · draft
          </div>
          <div style={{
            fontFamily: 'var(--ff-display)',
            fontSize: 56,
            letterSpacing: -2,
            marginTop: 12,
            lineHeight: 1,
            fontWeight: 400,
          }}>
            K{principalKwacha.toLocaleString()}
            <span style={{ fontSize: 26, opacity: 0.5 }}>.00</span>
          </div>
          <div style={{ fontSize: 13, opacity: 0.65, marginTop: 4 }}>
            {draft.termDays}-day term · {Math.round(cost.interest / Number(principalNgwee) * 100)}% interest · {formatEAR(cost.ear)}
          </div>

          <div style={{
            marginTop: 24, paddingTop: 18,
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}>
            {[
              ['Disbursed to', `${PROVIDER_DISPLAY_NAME[provider]} · ${profile ? shortMask(profile.phone) : ''}`],
              ['Processing fee', `K${ngweeToKwachaNumber(cost.processingFee).toLocaleString()}.00`],
              ['Total interest', `K${ngweeToKwachaNumber(cost.interest).toLocaleString()}.00`],
              ['Total repayment', `K${ngweeToKwachaNumber(cost.totalDue).toLocaleString()}.00`],
              ...schedule.map((s, i) => [
                i === 0 ? 'First payment' : i === schedule.length - 1 ? 'Final payment' : `Payment ${i + 1}`,
                `${shortDate(s.dueAt)} · K${ngweeToKwachaNumber(s.amount).toLocaleString()}.00`,
              ] as [string, string]),
            ].map(([label, value], i) => (
              <div key={`${label}-${i}`} style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '7px 0',
                fontSize: 13,
              }}>
                <span style={{ opacity: 0.6 }}>{label}</span>
                <span style={{ fontFamily: 'var(--ff-mono)' }}>{value}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{
                fontSize: 10, opacity: 0.5, letterSpacing: 1, textTransform: 'uppercase',
              }}>
                Reference
              </div>
              <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 13, marginTop: 2 }}>
                THND-NEW
              </div>
            </div>
            <div style={{ fontSize: 10, opacity: 0.4, fontFamily: 'var(--ff-mono)' }}>
              v1 · {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Provider picker — radio list */}
        <div style={{ marginTop: 22 }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Receive funds via</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SUPPORTED_MM_PROVIDERS.map((p) => {
              const selected = provider === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProvider(p)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    background: selected ? 'var(--c-accent-soft)' : 'var(--c-surface)',
                    border: `1px solid ${selected ? 'var(--c-accent)' : 'var(--c-border)'}`,
                    borderRadius: 14,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 14, color: 'var(--c-text)', fontWeight: 500 }}>
                    {PROVIDER_DISPLAY_NAME[p]}
                  </span>
                  <span style={{
                    width: 18, height: 18, borderRadius: 9,
                    border: `1.5px solid ${selected ? 'var(--c-accent)' : 'var(--c-border)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {selected && (
                      <span style={{
                        width: 8, height: 8, borderRadius: 4,
                        background: 'var(--c-accent)',
                      }} />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Agreement checkbox */}
        <label style={{
          display: 'flex', gap: 12,
          marginTop: 22, padding: '14px 16px',
          background: 'var(--c-surface)',
          border: `1px solid ${accepted ? 'var(--c-accent)' : 'var(--c-border)'}`,
          borderRadius: 14, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
          />
          <span style={{
            width: 22, height: 22, borderRadius: 6,
            background: accepted ? 'var(--c-accent)' : 'var(--c-surface-alt)',
            border: accepted ? 'none' : '1px solid var(--c-border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {accepted && <Check size={14} color="var(--c-accent-ink)" strokeWidth={2.6} />}
          </span>
          <span style={{ fontSize: 12, color: 'var(--c-text-sec)', lineHeight: 1.5 }}>
            I&apos;ve read the{' '}
            <span style={{
              color: 'var(--c-text)',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}>Loan Agreement</span>
            {' '}and authorise Thandizo to debit my mobile money on the dates above.
          </span>
        </label>
      </div>

      <div style={{ padding: '14px 24px 28px' }}>
        <Button
          block size="lg" loading={submitting} disabled={!accepted}
          onClick={submit}
          rightIcon={<ArrowRight size={18} />}
        >
          Sign &amp; disburse
        </Button>
        <div style={{
          marginTop: 10,
          textAlign: 'center',
          fontSize: 11,
          color: 'var(--c-text-dim)',
        }}>
          Funds arrive in your wallet within 5 minutes.
        </div>
      </div>
    </div>
  );
}
